import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore, lazy, Suspense, createContext, useContext } from 'react'
import { supabase, getSession, getMfaGate, fetchDeals, fetchUserProfile, fetchCreators, fetchLeads, createLead, fetchActivityLog, setActivityUser, signOut, fetchTeam } from './lib/supabase'
import { getCrmPeople, getDealAgents } from './lib/team'
import { hasFeature } from './lib/constants'
import { useAppTimeTracker } from './lib/useAppTimeTracker'

// =============================================================================
// APP.JS — Root component for AgencyBase
// =============================================================================
// SECTIONS:
//   [TOAST]       Toast notification system (context + container)
//   [IMPORTS]     Component imports (eager + lazy)
//   [HOOKS]       Custom hooks (useIsMobile)
//   [CONFIG]      Static config (TABS); views and CRM tabs derive from the team
//   [HELPERS]     Pure helper functions (getScopedDeals)
//   [CRMHUB]     CRMHub wrapper component (sub-tab routing for CRM)
//   [APP]         Main App component
//     [APP.STATE]   State declarations
//     [APP.AUTH]    Auth + session effects
//     [APP.DATA]    Data loading (deals, creators, leads)
//     [APP.DERIVED] Derived state (currentDeals, scopedDeals, permissions)
//     [APP.HANDLERS] Event handlers (copy to CRM, convert lead)
//     [APP.RENDER]  JSX render (nav, main content, footer, overlays)
// =============================================================================

// --- [TOAST] Toast notification system ---
const ToastContext = createContext(() => {})
export const useToast = () => useContext(ToastContext)

function ToastContainer({ toasts, onDismiss }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column-reverse', gap: 8, pointerEvents: 'none' }}>
      {toasts.map(t => (
        <div key={t.id} className="pop-in" onClick={() => onDismiss(t.id)} style={{
          pointerEvents: 'auto', padding: '12px 20px', borderRadius: 10,
          background: t.type === 'error' ? 'rgba(239,68,68,0.95)' : t.type === 'warning' ? 'rgba(245,158,11,0.95)' : 'rgba(16,185,129,0.95)',
          color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          backdropFilter: 'blur(8px)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          maxWidth: 360, fontFamily: 'var(--font-body)'
        }}>
          {t.message}
        </div>
      ))}
    </div>
  )
}
// --- [IMPORTS] Eager + lazy component imports ---
import Login from './components/Login'
import Dashboard from './components/Dashboard'
import Deals from './components/Deals'
import Creators from './components/Creators'
import CRM from './components/CRM'
import PersonalCRM from './components/PersonalCRM'
import GlobalSearch from './components/GlobalSearch'
import GoalTracker from './components/GoalTracker'
import AceChat from './components/AceChat'
import NotificationBell from './components/NotificationBell'

// Lazy-load heavy / admin-only components (code-split for faster initial load)
const Reports = lazy(() => import('./components/Reports'))
const Agents = lazy(() => import('./components/Agents'))
const AdminPanel = lazy(() => import('./components/AdminPanel'))
const Settings = lazy(() => import('./components/Settings'))
const ActivityLog = lazy(() => import('./components/ActivityLog'))
const TeamAnalytics = lazy(() => import('./components/TeamAnalytics'))
const SocialAnalytics = lazy(() => import('./components/SocialAnalytics'))
const FirstLook = lazy(() => import('./components/FirstLook'))
const DataHealth = lazy(() => import('./components/DataHealth'))

// --- [HOOKS] Custom hooks ---
const mq = typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)') : null
function useIsMobile() {
  const subscribe = useCallback(cb => { if (!mq) return () => {}; mq.addEventListener('change', cb); return () => mq.removeEventListener('change', cb) }, [])
  return useSyncExternalStore(subscribe, () => mq ? mq.matches : false)
}

// --- [CONFIG] Static configuration ---
const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'deals',     label: 'Deals' },
  { id: 'reports',   label: 'Reports' },
  { id: 'creators',  label: 'Creators' },
  { id: 'crm',       label: 'CRM' },
  { id: 'firstlook', label: 'First Look' },
  { id: 'todo',      label: 'To-do' },
  { id: 'activity',  label: 'Activity', adminOnly: true },
  { id: 'agents',    label: 'Agents',  adminOnly: true },
  { id: 'team',      label: 'Team',    adminOnly: true },
  { id: 'settings',  label: 'Settings', adminOnly: true },
]

// The "view as" switcher. Admin and Steve's creator hat are fixed; the agent
// entries come from the team table, so a new agent shows up here on their own.
const ADMIN_VIEW    = { id: 'admin',         label: 'Admin',          agentFilter: null,    creatorFilter: null    }
const STEVE_CREATOR = { id: 'steve_creator', label: 'Steve (creator)', agentFilter: null,    creatorFilter: 'Steve' }

function buildViews(team) {
  const agents = getDealAgents(team)
  // Steve sits next to his creator view; everyone else follows alphabetically.
  const ordered = [
    ...agents.filter(n => n === 'Steve'),
    ...agents.filter(n => n !== 'Steve').sort((a, b) => a.localeCompare(b)),
  ]
  return [
    ADMIN_VIEW,
    STEVE_CREATOR,
    ...ordered.map(name => ({
      id: name === 'Steve' ? 'steve_agent' : name.toLowerCase(),
      label: name === 'Steve' ? 'Steve (agent)' : name,
      agentFilter: name,
      creatorFilter: null,
    })),
  ]
}

// --- [HELPERS] Pure helper functions ---
function getScopedDeals(deals, view) {
  if (!view || view.id === 'admin') return deals
  let d = deals
  if (view.agentFilter) d = d.filter(x => x.agent === view.agentFilter)
  if (view.creatorFilter) d = d.filter(x => x.creator === view.creatorFilter && !x.agent)
  return d
}

// CRM_PEOPLE is now derived from the `team` state (fetched via fetchTeam).
// See getCrmPeople() in lib/team.js.

// --- [CRMHUB] CRM wrapper — routes between Agency CRM and Personal CRMs ---
function CRMHub({ deals, scopedDeals, visibleDeals, leads, loadLeads, currentUser, isAdmin, isSales, isMobile, privacyMode, ownerLabel, onCopyToPersonal, onConvertToDeal, onCopyLeadToMyCRM, crmPeople }) {
  const [subTab, setSubTab] = useState('agency')

  // Admin + Sales: see all people's CRM tabs. Agents/creators: just agency + their own
  const myName = currentUser.agent_name || currentUser.full_name || ''
  const subtabs = (isAdmin || isSales)
    ? [{ id: 'agency', label: 'Agency CRM' }, ...(crmPeople || []).map(p => ({ id: p.id, label: `${p.name}'s CRM` }))]
    : [{ id: 'agency', label: 'Agency CRM' }, { id: 'mine', label: `${ownerLabel}'s CRM` }]

  // For admin viewing a person's CRM, filter leads by that person's name
  const getPersonLeads = (personName) => leads.filter(l => l.owner_name === personName)
  const getPersonId = (personName) => {
    // Find from leads data or just use name matching
    const personLead = leads.find(l => l.owner_name === personName)
    return personLead?.owner_id || null
  }

  // Deals for agency CRM: admin & sales see all, agents see everything they can
  // SEE — not just what is theirs. The brand/contact directory is built from
  // this list, so using scopedDeals left an agent who has taken an account over
  // with a blank CRM until they closed their first deal, even though the
  // account's whole brand history was sitting right there in the Deals tab.
  const agencyDeals = (isAdmin || isSales) ? deals : (visibleDeals || scopedDeals)

  const tabStyle = (active) => ({
    padding: isMobile ? '8px 12px' : '8px 16px', border: 'none', cursor: 'pointer',
    background: active ? 'rgba(249,115,22,0.1)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text3)',
    fontWeight: active ? 700 : 500, fontSize: 12,
    borderRadius: 8, fontFamily: 'var(--font-body)',
    transition: 'all 0.15s', whiteSpace: 'nowrap'
  })

  return (
    <div>
      {/* Sub-tab navigation */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 20, padding: '4px',
        background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)',
        overflowX: 'auto', WebkitOverflowScrolling: 'touch'
      }}>
        {subtabs.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} style={tabStyle(subTab === st.id)}>
            {st.label}
          </button>
        ))}
      </div>

      {/* Agency CRM */}
      {subTab === 'agency' && (
        <CRM deals={agencyDeals} isMobile={isMobile} privacyMode={privacyMode} currentUser={currentUser} onCopyToPersonal={onCopyToPersonal} leads={leads} isAdmin={isAdmin} />
      )}

      {/* Personal CRM for non-admin (and non-sales since sales sees the per-person tabs instead) */}
      {subTab === 'mine' && !isAdmin && !isSales && (
        <PersonalCRM leads={leads.filter(l => l.owner_id === currentUser.id)} onRefresh={loadLeads} currentUser={currentUser} isMobile={isMobile} privacyMode={privacyMode} ownerLabel={ownerLabel} onConvertToDeal={onConvertToDeal} deals={agencyDeals} />
      )}

      {/* Admin or Sales viewing a specific person's CRM */}
      {(isAdmin || isSales) && (crmPeople || []).map(p => (
        subTab === p.id && (
          <PersonalCRM
            key={p.id}
            leads={getPersonLeads(p.name)}
            onRefresh={loadLeads}
            currentUser={{ ...currentUser, id: p.uid, agent_name: p.name }}
            isMobile={isMobile}
            privacyMode={privacyMode}
            ownerLabel={p.name}
            onConvertToDeal={onConvertToDeal}
            onCopyLeadToMyCRM={onCopyLeadToMyCRM}
            deals={agencyDeals}
          />
        )
      ))}
    </div>
  )
}

export default function App() {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [deals, setDeals] = useState([])
  const [creators, setCreators] = useState([])
  const [team, setTeam] = useState([])
  const [tab, setTab] = useState('dashboard')
  const [loading, setLoading] = useState(true)
  const [dealsLoading, setDealsLoading] = useState(false)
  const [viewId, setViewId] = useState('admin')
  const [showViewMenu, setShowViewMenu] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [leads, setLeads] = useState([])
  const [privacyMode, setPrivacyMode] = useState(false)
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') !== 'light')
  const [menuOpen, setMenuOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [toasts, setToasts] = useState([])
  const [convertLead, setConvertLead] = useState(null)
  const [focusDealId, setFocusDealId] = useState(null)
  // A notification click opens that First Look document on arrival.
  const [focusFirstLookId, setFocusFirstLookId] = useState(null)
  const clearFirstLookFocus = useCallback(() => setFocusFirstLookId(null), [])
  // Which datasets failed to load. Previously every loader did
  // `if (!error && data) set(data)` and threw the error away, so a failed
  // fetch left an empty list that looked exactly like an empty agency —
  // no spinner, no message, and totals quietly computed from nothing.
  const [loadErrors, setLoadErrors] = useState({})
  // Second-factor gate. signInWithPassword() hands back a session right away,
  // even when TOTP is enrolled — it is simply at assurance level aal1. Because
  // Login was rendered only while `session` was null, that session unlocked the
  // whole dashboard the instant the password was accepted, and the MFA screen
  // behind it decided nothing. 'checking' holds the UI until the level is known
  // so the app never flashes the dashboard on the way to the code prompt.
  const [mfaGate, setMfaGate] = useState('checking') // 'checking' | 'required' | 'satisfied'
  const hasLoadedRef = useRef(false)
  // Lifted Deals filter state so it persists across tab switches
  const [dealFilters, setDealFilters] = useState({
    search: '', filterCreator: 'All', filterAgent: 'All', filterStatus: 'All',
    filterMonth: 'All', filterBrand: 'All', filterClient: 'All', filterYear: String(new Date().getFullYear()),
    sortField: 'status', sortDir: 'asc', dealView: 'table', showFilters: false,
    hiddenCols: new Set(['contact', 'commission'])
  })
  const menuRef = useRef()
  const profileRef = useRef()

  const toast = useCallback((message, type = 'success') => {
    const id = Date.now()
    setToasts(t => [...t, { id, message, type }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500)
  }, [])
  const dismissToast = useCallback((id) => setToasts(t => t.filter(x => x.id !== id)), [])
  const handleConvertToDeal = useCallback((lead) => {
    setConvertLead(lead)
    setTab('deals')
  }, [])

  // Global search used to hand back only a tab name, so picking a specific deal
  // dropped you on the deals list to find it again by hand. Open the record.
  //
  // Filters are cleared on the way through on purpose: the Deals tab renders the
  // *filtered* list and looks the deal up inside it, so a hit from another year
  // (the year filter defaults to the current one) or another agent would not be
  // there and the modal would come up empty.
  const handleSearchNavigate = useCallback((result) => {
    if (!result) return
    if (result.type === 'deal' && result.id) {
      setDealFilters(f => ({
        ...f,
        search: '', filterCreator: 'All', filterAgent: 'All', filterStatus: 'All',
        filterMonth: 'All', filterBrand: 'All', filterClient: 'All', filterYear: 'All',
      }))
      setFocusDealId(result.id)
      setTab('deals')
      return
    }
    // Brands are an aggregate of deals rather than a record of their own, so the
    // useful destination is the deals list filtered down to that brand.
    if (result.type === 'brand' && result.id) {
      setDealFilters(f => ({
        ...f,
        search: '', filterCreator: 'All', filterAgent: 'All', filterStatus: 'All',
        filterMonth: 'All', filterClient: 'All', filterYear: 'All',
        filterBrand: result.id,
      }))
      setTab('deals')
      return
    }
    setTab(result.tab || 'dashboard')
  }, [])
  const isMobile = useIsMobile()

  const VIEWS = useMemo(() => buildViews(team), [team])
  const currentView = VIEWS.find(v => v.id === viewId) || VIEWS[0]

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowViewMenu(false)
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light')
    localStorage.setItem('theme', darkMode ? 'dark' : 'light')
  }, [darkMode])

  // ⌘K global search shortcut (admin only, Growth+ tier — gated in handler via profile ref)
  const profileRef2 = useRef(null)
  useEffect(() => { profileRef2.current = profile }, [profile])
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (profileRef2.current?.role === 'admin' && hasFeature(profileRef2.current?.org_tier, 'global_search')) setShowSearch(s => !s)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    getSession().then(s => { setSession(s); if (!s) setLoading(false) })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // Ignore token refreshes — they fire when returning to the tab
      if (event === 'TOKEN_REFRESHED') return
      // Returning to the tab also fires SIGNED_IN for the same user. Keeping the
      // same session object means nothing downstream re-runs: before this, every
      // tab switch re-ran the sign-in check and the profile load, which blanked
      // the whole app ("Checking sign-in...", "Loading AgencyBase...") and
      // closed whatever was open. The app only reads the user's id and email
      // from it; the Supabase client keeps the live tokens itself.
      setSession(prev => (s && prev?.user?.id === s.user?.id ? prev : s))
      // Only show loading spinner on initial login, not when returning to the tab.
      // setLoading(true) unmounts the entire UI (including open modals), so we must
      // avoid it when the user is already logged in and just switching browser tabs.
      if (s && !hasLoadedRef.current) { setLoading(true) }
      else if (!s) { setProfile(null); setDeals([]); setLoading(false); hasLoadedRef.current = false }
    })
    return () => subscription.unsubscribe()
  }, [])

  // Declared ahead of every effect that lists it as a dependency. Dependency
  // arrays are evaluated during render, so referencing a const declared
  // further down throws a TDZ ReferenceError on every render — which took the
  // whole app down on 2026-10-06 when this sat below the profile effect.
  //
  // Records the outcome of a load instead of discarding it, so a failure can
  // be shown and retried rather than silently rendering as "no data".
  const noteLoad = useCallback((key, error) => {
    setLoadErrors(prev => {
      const message = error ? (error.message || 'Could not load') : null
      if (!message && !prev[key]) return prev
      const next = { ...prev }
      if (message) next[key] = message
      else delete next[key]
      return next
    })
  }, [])

  const checkMfaGate = useCallback(async () => {
    const { required, error } = await getMfaGate()
    // If the level cannot be read, fail CLOSED when a session exists: better to
    // ask for a code that turns out to be unnecessary than to open the app on
    // an unverified session.
    if (error) { setMfaGate('required'); return 'required' }
    const next = required ? 'required' : 'satisfied'
    setMfaGate(next)
    return next
  }, [])

  useEffect(() => {
    if (!session) { setMfaGate('satisfied'); return }
    setMfaGate('checking')
    let cancelled = false
    checkMfaGate().then(() => { if (cancelled) { /* ignore */ } })
    return () => { cancelled = true }
  }, [session, checkMfaGate])

  // Only refetch profile when the actual user ID changes, not on every session reference change
  const currentUserId = session?.user?.id
  useEffect(() => {
    if (currentUserId && mfaGate === 'satisfied') {
      // Full-screen loader on first load only; later reloads update in place.
      if (!hasLoadedRef.current) setLoading(true)
      fetchUserProfile(currentUserId).then(({ data, error }) => {
        noteLoad('profile', error)
        setProfile(data)
        setActivityUser(data ? { ...data, id: currentUserId, email: session.user.email } : null)
        setViewId(data?.role === 'admin' ? 'admin' : 'agent')
        if (data?.role === 'sales') setTab('crm')
        setLoading(false)
        hasLoadedRef.current = true
      }).catch((err) => { noteLoad('profile', err); setLoading(false) })
    }
  }, [currentUserId, mfaGate, noteLoad])

  const loadDeals = useCallback(async () => {
    setDealsLoading(true)
    const { data, error, truncated } = await fetchDeals()
    if (!error && data) setDeals(data)
    // Truncation is reported like a failure, because for the totals on screen
    // it is one: a partial list computes a confidently wrong number.
    noteLoad('deals', error || (truncated ? { message: 'deals (list was cut short)' } : null))
    setDealsLoading(false)
  }, [noteLoad])  // fetchDeals doesn't depend on profile or session

  const loadTeam = useCallback(async () => {
    const { data, error } = await fetchTeam()
    if (!error && data) setTeam(data)
    noteLoad('team', error)
  }, [noteLoad])

  const loadCreators = useCallback(async () => {
    const { data, error, truncated } = await fetchCreators()
    if (!error && data) setCreators(data)
    noteLoad('creators', error || (truncated ? { message: 'creators (list was cut short)' } : null))
  }, [noteLoad])

  const loadLeads = useCallback(async () => {
    const { data, error, truncated } = await fetchLeads()
    if (!error && data) setLeads(data)
    noteLoad('leads', error || (truncated ? { message: 'leads (list was cut short)' } : null))
  }, [noteLoad])

  const retryFailedLoads = useCallback(() => {
    const failed = Object.keys(loadErrors)
    const jobs = []
    if (failed.includes('deals')) jobs.push(loadDeals())
    if (failed.includes('creators')) jobs.push(loadCreators())
    if (failed.includes('leads')) jobs.push(loadLeads())
    if (failed.includes('team')) jobs.push(loadTeam())
    return Promise.all(jobs)
  }, [loadErrors, loadDeals, loadCreators, loadLeads, loadTeam])

  // Load data once when user is available — not on every reference change
  useEffect(() => {
    if (currentUserId && mfaGate === 'satisfied') {
      Promise.all([loadDeals(), loadCreators(), loadLeads(), loadTeam()])
    }
  }, [currentUserId, mfaGate, loadDeals, loadCreators, loadLeads, loadTeam])

  const crmPeople = useMemo(() => getCrmPeople(team), [team])

  // --- [APP.DERIVED] Derived state ---
  // These hooks MUST be before any early returns to satisfy React's rules of hooks
  const currentDeals = useMemo(() => deals.filter(d => !d.is_historical), [deals])

  // Derive user info (safe even when session is null — guarded below)
  const currentUser = session && profile ? { ...profile, id: session.user.id } : session ? { id: session.user.id, role: 'agent', agent_name: '', full_name: session.user.email } : { id: '', role: 'agent', agent_name: '', full_name: '' }
  const isAdmin = currentUser.role === 'admin'
  const isCreator = currentUser.role === 'creator'
  const isSales = currentUser.role === 'sales'

  // Time-in-app for the Team tab. Staff only, and only once fully signed in;
  // the database ignores anyone else anyway.
  useAppTimeTracker(mfaGate === 'satisfied' && ['admin', 'agent', 'sales'].includes(profile?.role))

  // Force sales users to CRM tab only (effect avoids setting state during render)
  // (sales can navigate freely between dashboard, deals, and CRM)

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>AB</div>
        <div className="loading" style={{ fontSize: 13, color: 'var(--text3)' }}>Loading AgencyBase...</div>
      </div>
    </div>
  )

  if (!session) return <Login onLogin={s => setSession(s)} />

  // A session that has not cleared its second factor gets the code screen, not
  // the app. onLogin re-reads the assurance level rather than trusting the
  // callback, so the only way past here is a verified factor.
  if (mfaGate === 'checking') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div className="loading" style={{ fontSize: 13, color: 'var(--text3)' }}>Checking sign-in...</div>
    </div>
  )
  if (mfaGate === 'required') return (
    <Login
      startInMfa
      onLogin={async () => {
        const next = await checkMfaGate()
        if (next === 'satisfied') setSession(await getSession())
      }}
    />
  )

  // Tier / plan — defaults to null which grants all features (backwards compat for GSD)
  const orgTier = currentUser.org_tier || null
  const can = (feature) => hasFeature(orgTier, feature)

  // For non-admin, scope deals to their agent name or creator name.
  // Sales role sees everything (follow-up oversight) like admin.
  const scopedDeals = (isAdmin || isSales)
    ? getScopedDeals(currentDeals, currentView)
    : isCreator
      ? currentDeals.filter(d => d.creator === currentUser.agent_name)
      : currentDeals.filter(d => d.agent === currentUser.agent_name || d.creator === currentUser.agent_name)

  // Whose lens are we looking through? For an admin using the view-as switcher
  // that's the agent being viewed, not the admin — otherwise "view as Katrina"
  // would quietly use Steve's roster.
  // Plain computations, not hooks: everything from here down sits below the
  // loading/session early returns, so a hook here would change the hook count
  // between renders and crash the app.
  const effectiveAgent = (isAdmin ? currentView.agentFilter : currentUser?.agent_name) || null

  // Only widen when we're actually looking through an agent's eyes. Admin view
  // already shows everything, and "Steve (creator)" is meant to stay narrow.
  const viewingAsAgent = isAdmin ? Boolean(currentView.agentFilter) : (!isCreator && !isSales)

  // Creators that agent manages today, regardless of who closed the past deals.
  const managedCreators = new Set(
    (creators || [])
      .filter(c => c.default_agent && c.default_agent === effectiveAgent)
      .map(c => c.name)
  )

  // Two different questions, deliberately kept apart:
  //   scopedDeals  — what is MINE. Drives the dashboard, reports and targets.
  //   visibleDeals — what I can SEE. Adds the back catalogue for creators I
  //                  now manage, so taking over an account comes with its
  //                  history instead of a blank slate.
  // Widening the first would credit an agent with deals someone else closed.
  const visibleDeals = (() => {
    if (!viewingAsAgent || !managedCreators.size) return scopedDeals
    const seen = new Set(scopedDeals.map(d => d.id))
    return [
      ...scopedDeals,
      ...currentDeals.filter(d => !seen.has(d.id) && managedCreators.has(d.creator)),
    ]
  })()

  const viewMode = ((currentView.id === 'admin' && isAdmin) || isSales) ? 'admin' : 'agent'

  const ownerLabel = currentUser.agent_name || currentUser.full_name || 'My'

  // --- [APP.HANDLERS] Event handlers ---
  const handleCopyToPersonalCRM = async (brand) => {
    // Copy a brand from Agency CRM to user's personal leads
    const newLead = {
      owner_id: currentUser.id,
      owner_name: ownerLabel,
      brand: brand.brand,
      client: brand.client || '',
      contact_name: brand.contacts?.[0]?.name || '',
      contact_email: brand.contacts?.[0]?.email || '',
      contact_phone: '',
      status: 'New Lead',
      priority: brand.dealCount >= 3 ? 'hot' : brand.dealCount >= 1 ? 'warm' : 'cold',
      source: 'agency_crm',
      source_brand: brand.brand,
      estimated_value: brand.totalRevenue || 0,
      notes: `Copied from Agency CRM — ${brand.dealCount} deal(s), ${brand.creators?.join(', ')} creator(s)`,
      next_follow_up: null,
      last_contacted: brand.lastDealDate || null,
      org_id: currentUser.org_id,
    }
    const { error } = await createLead(newLead)
    if (error) { console.error('Failed to copy lead:', error); alert('Failed to copy — ' + error.message); return }
    await loadLeads()
  }

  const handleCopyLeadToMyCRM = async (lead) => {
    const newLead = {
      owner_id: currentUser.id,
      owner_name: ownerLabel,
      brand: lead.brand || '',
      client: lead.client || '',
      contact_name: lead.contact_name || '',
      contact_email: lead.contact_email || '',
      contact_phone: lead.contact_phone || '',
      status: lead.status || 'New Lead',
      priority: lead.priority || 'warm',
      source: 'copied_from_' + (lead.owner_name || 'other'),
      estimated_value: lead.estimated_value || 0,
      notes: `Copied from ${lead.owner_name || 'other'}'s CRM` + (lead.notes ? ` — ${lead.notes}` : ''),
      next_follow_up: lead.next_follow_up || null,
      last_contacted: lead.last_contacted || null,
      org_id: currentUser.org_id,
    }
    const { error } = await createLead(newLead)
    if (error) { toast('Failed to copy lead: ' + error.message, 'error'); return }
    toast(`Lead "${lead.brand || lead.client}" copied to your CRM`)
    await loadLeads()
  }

  // --- [APP.RENDER] ---
  return (
    <ToastContext.Provider value={toast}>
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* Privacy banner */}
      {privacyMode && (
        <div style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', padding: '6px 24px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#fff' }}>
          Privacy Mode — amounts, commissions & client names are hidden . <button onClick={() => setPrivacyMode(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '2px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, marginLeft: 8 }}>Turn off</button>
        </div>
      )}

      {/* Nav */}
      <nav style={{ background: 'var(--nav-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid var(--card-border)', padding: isMobile ? '0 12px' : '0 24px', position: 'sticky', top: 0, zIndex: 50, boxShadow: 'var(--shadow-xs)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
          {/* Brand */}
          <div onClick={() => setTab(isSales ? 'deals' : 'dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)', flexShrink: 0 }}>AB</div>
            {!isMobile && (
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, letterSpacing: '-0.3px' }}>AgencyBase</div>
            )}
          </div>

          {/* Desktop Tabs -- hide admin-only tabs when viewing as agent */}
          {!isMobile && (() => {
            const visibleTabs = TABS.filter(t => {
              if (isSales) return t.id === 'deals' || t.id === 'crm' || t.id === 'reports' || t.id === 'firstlook' || t.id === 'todo'
              if (isCreator) return t.id === 'dashboard' || t.id === 'deals' || t.id === 'creators'
              if (!isAdmin) return !t.adminOnly
              if (viewId !== 'admin') return !t.adminOnly
              return true
            })
            const mainTabs = visibleTabs.filter(t => !t.adminOnly)
            const adminTabs = visibleTabs.filter(t => t.adminOnly)
            const renderTab = ({ id, label }) => (
              <button key={id} onClick={() => setTab(id)} style={{
                padding: '6px 14px', border: 'none', cursor: 'pointer', background: 'transparent',
                color: tab === id ? 'var(--text)' : 'var(--text3)',
                fontWeight: tab === id ? 600 : 400, fontSize: 13,
                borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
                fontFamily: 'var(--font-body)', transition: 'all 0.2s var(--ease)',
                boxShadow: tab === id ? '0 2px 8px rgba(249,115,22,0.15)' : 'none'
              }}>{label}</button>
            )
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {mainTabs.map(renderTab)}
                {adminTabs.length > 0 && (
                  <>
                    <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 6px' }} />
                    <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginRight: 2, opacity: 0.6 }}>Admin</span>
                    {adminTabs.map(renderTab)}
                  </>
                )}
              </div>
            )
          })()}

          {/* Right side -- compact */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8 }}>
            {dealsLoading && <div className="loading" style={{ fontSize: 11, color: 'var(--text3)' }}>Syncing...</div>}

            {/* Search (admin only, Growth+ tier) */}
            {isAdmin && can('global_search') && (
              <button onClick={() => setShowSearch(true)} title="Search (⌘K)" aria-label="Search" style={{
                padding: '5px 10px', borderRadius: 7, border: '1px solid var(--card-border)',
                background: 'transparent', color: 'var(--text3)', fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s var(--ease)'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                {!isMobile && <kbd style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 4, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text3)' }}>⌘K</kbd>}
              </button>
            )}

            {/* Notifications — staff only; creators have nothing routed to them */}
            {!isCreator && (
              <NotificationBell isMobile={isMobile} onOpen={n => {
                if (n.entity_type === 'first_look_document' && n.entity_id) {
                  setFocusFirstLookId(n.entity_id)
                  setTab('firstlook')
                }
              }} />
            )}

            {/* Privacy toggle */}
            <button onClick={() => setPrivacyMode(p => !p)} title={privacyMode ? 'Privacy ON — click to show data' : 'Privacy OFF — click to hide sensitive data'} aria-label={privacyMode ? 'Disable privacy mode' : 'Enable privacy mode'} style={{
              padding: '5px 10px', borderRadius: 7, border: '1px solid var(--card-border)',
              background: privacyMode ? 'rgba(249,115,22,0.12)' : 'transparent',
              color: privacyMode ? 'var(--accent)' : 'var(--text3)', fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s var(--ease)'
            }}>
              {privacyMode ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              {!isMobile && <span style={{ fontSize: 11, fontWeight: 600 }}>{privacyMode ? 'Private' : ''}</span>}
            </button>

            {/* View switcher -- admin only, hide on mobile */}
            {isAdmin && !isMobile && (
              <div ref={menuRef} style={{ position: 'relative' }}>
                <button className="btn-hover" onClick={() => setShowViewMenu(s => !s)} style={{
                  padding: '5px 12px', borderRadius: 7, border: '1px solid var(--card-border)',
                  background: viewId !== 'admin' ? 'rgba(249,115,22,0.08)' : 'transparent',
                  color: 'var(--text)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6
                }}>
                  {currentView.label} <span style={{ fontSize: 10, color: 'var(--text3)' }}>v</span>
                </button>
                {showViewMenu && (
                  <div className="pop-in" style={{
                    position: 'absolute', top: '110%', right: 0, background: 'var(--nav-bg)',
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid var(--card-border)', borderRadius: 10, padding: 6,
                    minWidth: 200, zIndex: 100, boxShadow: 'var(--shadow-lg)'
                  }}>
                    {VIEWS.map(v => (
                      <button key={v.id} onClick={() => {
                        setViewId(v.id); setShowViewMenu(false)
                        // Reset to dashboard if current tab is admin-only and switching to agent view
                        if (v.id !== 'admin') {
                          const adminTabs = TABS.filter(t => t.adminOnly).map(t => t.id)
                          if (adminTabs.includes(tab)) setTab('dashboard')
                        }
                      }} style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                        border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
                        background: viewId === v.id ? 'rgba(249,115,22,0.1)' : 'transparent',
                        color: viewId === v.id ? 'var(--accent)' : 'var(--text)', fontWeight: viewId === v.id ? 600 : 400,
                        fontFamily: 'var(--font-body)'
                      }}>{v.label}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Profile avatar dropdown */}
            {!isMobile && (
              <div ref={profileRef} style={{ position: 'relative' }}>
                <button onClick={() => setShowProfileMenu(s => !s)} aria-label="Profile menu" style={{
                  width: 34, height: 34, borderRadius: '50%', border: '1.5px solid var(--card-border)',
                  background: showProfileMenu ? 'rgba(249,115,22,0.12)' : 'var(--bg3)',
                  color: showProfileMenu ? 'var(--accent)' : 'var(--text)', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--font-display)', transition: 'all 0.2s var(--ease)'
                }}>
                  {(currentUser.full_name || session.user.email || '?')[0].toUpperCase()}
                </button>
                {showProfileMenu && (
                  <div className="pop-in" style={{
                    position: 'absolute', top: '110%', right: 0, background: 'var(--nav-bg)',
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid var(--card-border)', borderRadius: 10, padding: 6,
                    minWidth: 200, zIndex: 100, boxShadow: 'var(--shadow-lg)'
                  }}>
                    {/* User info header */}
                    <div style={{ padding: '8px 12px 10px', borderBottom: '1px solid var(--card-border)', marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{currentUser.full_name || session.user.email}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{isAdmin ? (viewId === 'admin' ? 'Admin' : `Viewing as ${currentView.label}`) : isCreator ? 'Creator' : isSales ? 'Sales' : 'Agent'}</div>
                    </div>
                    {/* Theme toggle */}
                    <button onClick={() => { setDarkMode(d => !d); setShowProfileMenu(false) }} style={{
                      display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px',
                      border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
                      background: 'transparent', color: 'var(--text)', fontFamily: 'var(--font-body)'
                    }}>
                      <span>{darkMode ? 'Light mode' : 'Dark mode'}</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>{darkMode ? 'OFF' : 'ON'}</span>
                    </button>
                    {/* Divider + Sign out */}
                    <div style={{ borderTop: '1px solid var(--card-border)', marginTop: 4, paddingTop: 4 }}>
                      <button onClick={() => signOut()} style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                        border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
                        background: 'transparent', color: 'var(--accent2)', fontFamily: 'var(--font-body)'
                      }}>Sign out</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile hamburger */}
            {isMobile && (
              <button onClick={() => setMenuOpen(m => !m)} aria-label={menuOpen ? 'Close menu' : 'Open menu'} style={{
                padding: '6px 8px', borderRadius: 7, border: '1px solid var(--border)',
                background: menuOpen ? 'rgba(249,115,22,0.12)' : 'transparent',
                color: 'var(--text)', cursor: 'pointer', lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {menuOpen
                  ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {isMobile && menuOpen && (
        <div className="mobile-drawer" style={{
          position: 'fixed', top: 56, left: 0, right: 0, bottom: 0,
          background: 'var(--bg2)', zIndex: 49, padding: 16,
          overflowY: 'auto', borderTop: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Navigation</div>
          {TABS.filter(t => {
            if (isSales) return t.id === 'deals' || t.id === 'crm' || t.id === 'reports' || t.id === 'firstlook' || t.id === 'todo'
            if (isCreator) return t.id === 'dashboard' || t.id === 'deals' || t.id === 'creators'
            if (!isAdmin) return !t.adminOnly
            return true
          }).map(({ id, label }) => (
            <button key={id} onClick={() => { setTab(id); setMenuOpen(false) }} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, marginBottom: 2,
              background: tab === id ? 'rgba(249,115,22,0.1)' : 'transparent',
              color: tab === id ? 'var(--accent)' : 'var(--text)',
              fontWeight: tab === id ? 700 : 400, fontFamily: 'var(--font-body)'
            }}>{label}</button>
          ))}

          {isAdmin && (
            <>
              <div style={{ borderTop: '1px solid var(--border)', margin: '12px 0', paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>View As</div>
                {VIEWS.map(v => (
                  <button key={v.id} onClick={() => { setViewId(v.id); setShowViewMenu(false) }} style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                    border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, marginBottom: 2,
                    background: viewId === v.id ? 'rgba(249,115,22,0.1)' : 'transparent',
                    color: viewId === v.id ? 'var(--accent)' : 'var(--text)',
                    fontWeight: viewId === v.id ? 600 : 400, fontFamily: 'var(--font-body)'
                  }}>{v.label}</button>
                ))}
              </div>
            </>
          )}

          <div style={{ borderTop: '1px solid var(--border)', margin: '12px 0', paddingTop: 12 }}>
            <button onClick={() => { setPrivacyMode(p => !p); setMenuOpen(false) }} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13,
              background: privacyMode ? 'rgba(99,102,241,0.12)' : 'transparent',
              color: privacyMode ? '#8b5cf6' : 'var(--text3)', fontWeight: 600
            }}>
              {privacyMode ? 'Privacy ON' : 'Privacy mode'}
            </button>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', margin: '12px 0', paddingTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{currentUser.full_name || session.user.email}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>{isAdmin ? 'Admin' : isCreator ? 'Creator' : isSales ? 'Sales' : 'Agent'}</div>
            <button onClick={() => signOut()} style={{
              padding: '10px 18px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'transparent', color: 'var(--text3)', fontSize: 13, cursor: 'pointer', width: '100%'
            }}>
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <main style={{ flex: 1, maxWidth: 1280, margin: '0 auto', width: '100%', padding: isMobile ? '16px 12px' : '28px 24px' }}>
        {Object.keys(loadErrors).length > 0 && (
          <div role="alert" style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            marginBottom: 18, padding: '12px 16px', borderRadius: 'var(--radius-lg)',
            background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
          }}>
            <span style={{ fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 220 }}>
              Could not load {Object.keys(loadErrors).join(', ')}. The numbers on this
              screen are incomplete.
            </span>
            <button onClick={retryFailedLoads} style={{
              padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: '#ef4444', color: '#fff', border: 'none',
            }}>
              Retry
            </button>
          </div>
        )}
        {tab === 'dashboard' && (
          <>
            {can('goal_tracking') && <GoalTracker deals={scopedDeals} isAdmin={isAdmin} isMobile={isMobile} currentUser={currentUser} privacyMode={privacyMode} />}
            {can('goal_tracking') && <div style={{ height: 20 }} />}
            <Dashboard deals={currentDeals} viewMode={viewMode} currentUser={currentUser} scopedDeals={scopedDeals} activeAgentName={currentView.agentFilter || (currentView.creatorFilter ? currentView.creatorFilter : null)} isMobile={isMobile} isCreator={isCreator} creators={creators} privacyMode={privacyMode} leads={leads} isAdmin={isAdmin} setTab={setTab} />
          </>
        )}
        <div style={{ display: tab === 'deals' ? 'block' : 'none' }}>
          <Deals deals={visibleDeals} allDeals={deals} currentUser={currentUser} onRefresh={loadDeals} viewMode={viewMode} activeAgentName={currentView.agentFilter} isMobile={isMobile} privacyMode={privacyMode} filters={dealFilters} onFiltersChange={setDealFilters} convertLead={convertLead} onConvertLeadHandled={() => setConvertLead(null)} focusDealId={focusDealId} onFocusDealHandled={() => setFocusDealId(null)} team={team} managedCreators={managedCreators} onToast={toast} />
        </div>
        {tab === 'reports'   && <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading reports...</div>}><Reports deals={scopedDeals} viewMode={viewMode} currentUser={currentUser} isMobile={isMobile} privacyMode={privacyMode} team={team} /></Suspense>}
        <div style={{ display: tab === 'creators' ? 'block' : 'none' }}>
          <Creators deals={visibleDeals} allDeals={currentDeals} creators={creators} onRefreshCreators={loadCreators} currentUser={currentUser} viewMode={viewMode} activeAgentName={currentView.agentFilter} isMobile={isMobile} privacyMode={privacyMode} />
        </div>
        {!isCreator && (
          <div style={{ display: tab === 'crm' ? 'block' : 'none' }}>
            <CRMHub deals={currentDeals} scopedDeals={scopedDeals} visibleDeals={visibleDeals} leads={leads} loadLeads={loadLeads} currentUser={currentUser} isAdmin={isAdmin} isSales={isSales} isMobile={isMobile} privacyMode={privacyMode} ownerLabel={ownerLabel} onCopyToPersonal={handleCopyToPersonalCRM} onConvertToDeal={handleConvertToDeal} onCopyLeadToMyCRM={handleCopyLeadToMyCRM} crmPeople={crmPeople} />
          </div>
        )}
        {tab === 'firstlook' && !isCreator && (
          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading First Look...</div>}>
            <FirstLook currentUser={currentUser} team={team} deals={currentDeals} isMobile={isMobile} privacyMode={privacyMode}
              focusDocumentId={focusFirstLookId} onFocusHandled={clearFirstLookFocus} />
          </Suspense>
        )}
        {tab === 'todo' && !isCreator && (
          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>}>
            <DataHealth deals={(isAdmin || isSales) ? currentDeals : scopedDeals} leads={leads} creators={creators} team={team}
              currentUser={currentUser} isMobile={isMobile} privacyMode={privacyMode}
              onOpenDeal={id => handleSearchNavigate({ type: 'deal', id })} onOpenCrm={() => setTab('crm')} />
          </Suspense>
        )}
        {tab === 'activity'  && isAdmin && <Suspense fallback=""><ActivityLog isMobile={isMobile} privacyMode={privacyMode} /></Suspense>}
        {tab === 'team'      && isAdmin && <Suspense fallback=""><TeamAnalytics team={team} isMobile={isMobile} /></Suspense>}
        {tab === 'agents'    && isAdmin && viewId === 'admin' && can('agent_performance') && <Suspense fallback=""><Agents deals={currentDeals} isMobile={isMobile} privacyMode={privacyMode} team={team} /></Suspense>}
        {tab === 'agents'    && isAdmin && viewId === 'admin' && !can('agent_performance') && <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}><div style={{ fontSize: 32, marginBottom: 12 }}>📊</div><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Agent Performance</div><div style={{ fontSize: 13 }}>Upgrade to the Growth plan to access agent performance dashboards and goal tracking.</div></div>}
        {tab === 'agents'    && isAdmin && viewId !== 'admin' && <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}><div style={{ fontSize: 32, marginBottom: 12 }}>Locked</div>Switch to Admin view to see agent performance.</div>}
        {tab === 'settings'  && isAdmin && <Suspense fallback=""><Settings onRefresh={loadDeals} AdminPanel={AdminPanel} currentUser={currentUser} /></Suspense>}
      </main>

      <footer style={{ borderTop: '1px solid var(--border)', padding: '12px 24px', textAlign: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>© {new Date().getFullYear()} AgencyBase. All rights reserved.</span>
      </footer>

      {/* Global Search Overlay */}
      {showSearch && (
        <GlobalSearch
          deals={deals} creators={creators} leads={leads}
          onNavigate={handleSearchNavigate}
          onClose={() => setShowSearch(false)}
          privacyMode={privacyMode}
        />
      )}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {/* Ace — AI partner (admin only) */}
      {isAdmin && <AceChat isMobile={isMobile} />}
    </div>
    </ToastContext.Provider>
  )
}
